import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import TextDropdownMenu from "@ats/src/components/shared/TextDropdownMenu";
import Icon from "@ats/src/components/shared/Icon";
import Tooltip from "@ats/src/components/shared/Tooltip";

export interface SearchableOption {
  key: string;
  label: string;
  isDisabled?: boolean;
}

interface Props {
  text: string;
  icon?: string;
  searchPlaceholder: string;
  options: SearchableOption[];
  onSelect: (key: string) => void;
  disabledTooltip?: string;
  OptionComponent?: React.ComponentType<{ option: SearchableOption }>;
}

const SearchableOptionsMenu: React.FunctionComponent<Props> = ({
  text,
  icon,
  searchPlaceholder,
  options,
  onSelect,
  disabledTooltip,
  OptionComponent,
}) => {
  const [query, setQuery] = React.useState("");

  const matchingOptions = options.filter((option) =>
    option.label.toLowerCase().includes(query.trim().toLowerCase()),
  );

  const listRef = React.useRef<HTMLDivElement>(null);

  const focusOption = (from: Element | null, step: number) => {
    const optionElements = Array.from(
      listRef.current.querySelectorAll<HTMLElement>('[role="option"]'),
    );
    const index = optionElements.indexOf(from as HTMLElement);
    const next = optionElements[index + step];
    if (next) next.focus();
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(null, 1);
    }
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      focusOption(event.currentTarget, event.key === "ArrowDown" ? 1 : -1);
    } else if (
      (event.key === "Enter" || event.key === " ") &&
      event.currentTarget.getAttribute("aria-disabled") !== "true"
    ) {
      event.preventDefault();
      event.currentTarget.click();
    }
  };

  const optionContent = (option: SearchableOption) =>
    OptionComponent ? <OptionComponent option={option} /> : option.label;

  return (
    <TextDropdownMenu text={text} icon={icon} caret={false} hpos="left" vpos="bottom">
      <Styled.Body>
        <Styled.Search onClick={(event) => event.stopPropagation()}>
          <Icon name="search" />
          <input
            value={query}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </Styled.Search>

        <Styled.List ref={listRef} role="listbox" aria-label={text}>
          {matchingOptions.length > 0 ? (
            matchingOptions.map((option) =>
              option.isDisabled ? (
                <Tooltip key={option.key} label={disabledTooltip}>
                  <Styled.DisabledOption
                    role="option"
                    aria-disabled="true"
                    tabIndex={-1}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={handleOptionKeyDown}
                  >
                    {optionContent(option)}
                  </Styled.DisabledOption>
                </Tooltip>
              ) : (
                <Styled.Option
                  key={option.key}
                  role="option"
                  tabIndex={0}
                  onClick={() => onSelect(option.key)}
                  onKeyDown={handleOptionKeyDown}
                >
                  {optionContent(option)}
                </Styled.Option>
              ),
            )
          ) : (
            <Styled.Empty>No matches</Styled.Empty>
          )}
        </Styled.List>
      </Styled.Body>
    </TextDropdownMenu>
  );
};

export default SearchableOptionsMenu;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Body = styled.div`
  label: SearchableOptionsMenu;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 300px;
`;

Styled.Search = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: SearchableOptionsMenu_Search;
    margin-left: -${t.spacing[1]};
    margin-right: -${t.spacing[1]};
    ${[t.h(8), t.px(3), t.mb(1)]}
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 7px;
    border-bottom: 1px solid ${t.dark ? t.color.gray[600] : t.color.gray[200]};
    color: ${t.color.gray[500]};

    svg {
      flex-shrink: 0;
      height: 15px;
      width: 15px;
    }

    input {
      ${t.text.sm}
      width: 100%;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      color: ${t.dark ? t.color.gray[200] : t.color.black};

      &::placeholder {
        color: ${t.color.gray[500]};
      }
    }
  `;
});

Styled.List = styled.div`
  label: SearchableOptionsMenu_List;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

Styled.Option = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: SearchableOptionsMenu_Option;
    ${[t.px(2), t.py(1), t.text.sm]}
    display: block;
    width: 100%;
    text-align: left;
    cursor: pointer;
    line-height: 1.25rem;
    color: ${t.dark ? t.color.gray[200] : t.color.black};

    &:hover,
    &:focus {
      outline: none;
      background-color: ${t.dark ? t.color.gray[600] : t.color.gray[100]};
    }
  `;
});

Styled.DisabledOption = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: SearchableOptionsMenu_DisabledOption;
    ${[t.px(2), t.py(1), t.text.sm]}
    display: block;
    width: 100%;
    text-align: left;
    cursor: default;
    opacity: 0.5;
    line-height: 1.25rem;
    color: ${t.dark ? t.color.gray[200] : t.color.black};

    &:focus {
      outline: none;
      background-color: ${t.dark ? t.color.gray[600] : t.color.gray[100]};
    }
  `;
});

Styled.Empty = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: SearchableOptionsMenu_Empty;
    ${[t.px(2), t.py(2), t.text.sm]}
    color: ${t.color.gray[500]};
  `;
});
